/**
 * Limpiar Producto — migración web de ares8080 (escritorio).
 *
 * Herramienta administrativa de HARD DELETE: elimina definitivamente un
 * préstamo registrado por error junto con su producto, facturas, solicitud
 * y amortización, dejando bitácora en controllimpieza. El backend rechaza
 * préstamos con pagos aplicados (devuelve data.Error). Corrige el WHERE
 * roto del escritorio que dejaba préstamos huérfanos a medio borrar.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function LimpiarProducto({ enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prestamo, setPrestamo] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [limpiando, setLimpiando] = useState(false);

  const cargarPrestamos = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/refinanciamiento_prestamos", {});
      if (ok) setPrestamos(data.prestamos || []);
      else
        showAlert({ message: data.Error || "No se pudieron cargar los préstamos", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiarPantalla = () => {
    setPrestamo(null);
    setConfirmOpen(false);
  };

  const ejecutarLimpieza = async () => {
    setLimpiando(true);
    try {
      const { ok, data } = await post("/api/limpiarproducto", { noprest: prestamo.noprest });
      if (ok) {
        showAlert({
          message: data.mensajeok || `Préstamo #${prestamo.noprest} eliminado definitivamente`,
          color: "success",
        });
        limpiarPantalla();
        cargarPrestamos();
      } else {
        showAlert({ message: data.Error || "No se pudo limpiar el producto", color: "error" });
        setConfirmOpen(false);
      }
    } finally {
      setLimpiando(false);
    }
  };

  const chipInfo = {
    bgcolor: FP.color.surfaceAlt,
    color: FP.color.textSecondary,
    fontWeight: 700,
  };

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Limpiar Producto"
        subtitle="Elimina definitivamente una venta registrada por error y sin pagos (ares8080)"
        cta={
          <Button
            startIcon={<RestartAltIcon />}
            onClick={limpiarPantalla}
            sx={btnOutline(FP.color.navy)}
          >
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <SectionTitle icon={SearchOutlinedIcon} color={FP.color.accentBlue}>
            Préstamo a Limpiar
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => setPrestamo(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest} — {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {prestamo && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Préstamo #${prestamo.noprest}`} sx={chipInfo} />
                  <Chip label={prestamo.nombres || "—"} sx={chipInfo} />
                  <Chip label={`Cédula ${prestamo.cedula || "—"}`} sx={chipInfo} />
                  {prestamo.chasis && <Chip label={`Chasis ${prestamo.chasis}`} sx={chipInfo} />}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Aviso permanente + acción */}
        <Box sx={{ ...sectionBox, mb: 0, borderColor: FP.color.danger }}>
          <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ mb: 2 }}>
            <WarningAmberOutlinedIcon sx={{ color: FP.color.danger, mt: 0.2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.danger, fontSize: "0.9rem" }}>
              Esta herramienta ELIMINA definitivamente el préstamo, su producto, facturas, solicitud
              y amortización. Solo para ventas registradas por error y sin pagos.
            </Typography>
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              startIcon={
                limpiando ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteForeverOutlinedIcon />
                )
              }
              onClick={() => setConfirmOpen(true)}
              disabled={!prestamo || limpiando}
              sx={btnSolid("#D32F2F", "#B71C1C")}
            >
              Limpiar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Confirmación con doble advertencia */}
      <Dialog
        open={confirmOpen}
        onClose={() => !limpiando && setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: FP.color.danger,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <WarningAmberOutlinedIcon />
          Eliminar definitivamente
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 1.5 }}>
            Está a punto de ELIMINAR el préstamo <b>#{prestamo?.noprest}</b> de{" "}
            <b>{prestamo?.nombres}</b> junto con su producto, facturas, solicitud y amortización.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: "0.9rem", fontWeight: 700, color: FP.color.danger }}>
            Esta acción NO se puede deshacer. ¿Está completamente seguro de que esta venta fue
            registrada por error y no tiene pagos?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={limpiando} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              limpiando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteForeverOutlinedIcon />
              )
            }
            onClick={ejecutarLimpieza}
            disabled={limpiando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, eliminar definitivamente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
